import { ReactNode } from "react";
import styled from "styled-components";
import { ActionComponent } from "../../../internalComponents";
import { SemanticIcon, SmallIcon } from "../../Icon";
import { BaseToastProps, ToastType } from "./types";
import { primaryActive } from "../../../typography/alpha";
import "../../../designTokens/variables.css";
import { Button, SecondaryButton } from "../../Button";

type TypeEntry = {
  backgroundColor: string;
  icon: ReactNode;
};

const toastTypeRegistry: Record<ToastType, TypeEntry> = {
  success: {
    backgroundColor: "var(--color-success-100)",
    icon: <SemanticIcon iconName="success" />,
  },
  info: {
    backgroundColor: "var(--color-secondary-200)",
    icon: <SemanticIcon iconName="information" />,
  },
  error: {
    backgroundColor: "var(--color-error-100)",
    icon: <SemanticIcon iconName="error" />,
  },
  warning: {
    backgroundColor: "var(--color-warning-100)",
    icon: <SemanticIcon iconName="warning" />,
  },
};

const StyledToast = styled.div`
  display: inline-grid;
  grid-template-columns: 40px 1fr 24px;
  column-gap: 12px;
  box-shadow: var(--elevation-4);
  border-radius: var(--size-border-radius-small);
  background: var(--color-neutral-000-full);
  width: 288px;
  color: var(--color-primary-900);
`;

type StyledIconAreaProps = {
  backgroundColor: string;
};

const StyledIconArea = styled.div<StyledIconAreaProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.backgroundColor};
  border-radius: 0px;
  border-top-left-radius: var(--size-border-radius-small);
  border-bottom-left-radius: var(--size-border-radius-small);
`;

const ToastContent = styled.div`
  overflow-wrap: anywhere;
  display: grid;
  row-gap: 12px;
  padding: 8px 0px;
`;

const Title = styled.header`
  ${primaryActive.body2};
`;

const Description = styled.article`
  ${primaryActive.body3};
`;

const StyledCloseButton = styled.button`
  display: inline-flex;
  margin: 0px;
  margin-top: 8px;
  padding: 0px;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const ActionContainer = styled.div`
  margin: 4px 0px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
`;

export const BaseToast = ({
  title,
  toastType,
  action,
  onClose,
  primaryButton,
  secondaryButton,
  children,
  className,
}: BaseToastProps) => {
  const { backgroundColor, icon } = toastTypeRegistry[toastType];

  return (
    <StyledToast className={className}>
      <StyledIconArea backgroundColor={backgroundColor}>{icon}</StyledIconArea>
      <ToastContent>
        <Title>{title}</Title>
        <Description>{children}</Description>
        {(primaryButton || secondaryButton) && (
          <ButtonContainer>
            {secondaryButton && (
              <SecondaryButton {...secondaryButton}>
                {secondaryButton.children}
              </SecondaryButton>
            )}
            {primaryButton && (
              <Button {...primaryButton}> {primaryButton.children}</Button>
            )}
          </ButtonContainer>
        )}

        {action && (
          <ActionContainer>
            <ActionComponent action={action} />
          </ActionContainer>
        )}
      </ToastContent>
      <div>
        {onClose && (
          <StyledCloseButton data-testid="close-button" onClick={onClose}>
            <SmallIcon iconName="close" />
          </StyledCloseButton>
        )}
      </div>
    </StyledToast>
  );
};
